import { useNavigate } from 'react-router-dom';
import { useCurrentUserProfile } from '@busy/api';
import { useT } from '../../lib/i18n';
import { missingRequired } from '../../lib/profileFields';

// "Finish your profile" nudge for the Today screen. Renders only when one of
// the leaderboard-required fields (first_name / zip / years_cutting) is
// missing or still on a legacy value. Dismissable per session via the close
// button — but only the *current* tab session, since localStorage would mean
// users who close it once never see the prompt again. Returning tomorrow,
// they'll see the nudge again until they fill the fields in.
//
// The banner doubles as the "you can't compete on the leaderboard until
// you finish this" cue when Phase 2 ships.

export function ProfileCompleteBanner() {
  const t = useT();
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  // Hide until profile loads — avoids a flash on cold start.
  if (profile.loading || !profile.data) return null;
  const missing = missingRequired({
    first_name: profile.data.first_name,
    zip: profile.data.zip,
    years_cutting: profile.data.years_cutting,
  });
  if (missing.length === 0) return null;
  const fieldList = missing.map((f) => t(`profile.field.${f}`)).join(', ');
  return (
    <section
      role="region"
      aria-label={t('today.profileBanner.title')}
      className="rounded-2xl overflow-hidden border border-gold-500/40 bg-gradient-to-br from-navy-900 to-navy-800 text-white shadow-md"
    >
      <div className="px-4 py-3.5 sm:px-5 sm:py-4 flex items-center gap-3">
        <span className="shrink-0 w-9 h-9 rounded-lg bg-gold-500/15 grid place-items-center text-gold-400">
          <UserPlusIcon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-400 mb-0.5">
            {t('today.profileBanner.eyebrow')}
          </div>
          <div className="text-sm font-semibold leading-snug">
            {t('today.profileBanner.title')}
          </div>
          <div className="text-[12px] text-white/70 leading-snug mt-0.5">
            {t('today.profileBanner.body', { fields: fieldList })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="shrink-0 h-9 px-3.5 rounded-lg bg-gold-500 text-navy-900 text-xs font-bold hover:bg-gold-400 transition-colors"
        >
          {t('today.profileBanner.cta')}
        </button>
      </div>
    </section>
  );
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
